import React, { useContext, useEffect } from 'react';
import { t } from '@lingui/macro';
import { useBreakpoint } from '@landr/maestro';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import useHasFilterApplied from './hooks/useHasFilterApplied';
import DefaultSearchBarContext from './default';
import useSuggestions from './hooks/useSuggestions';
import useInput from './hooks/useInput';
import useSelectSuggestion from './hooks/useSelectSuggestion';
import useUserRecentSearchSuggestions from './hooks/useUserRecentSearchSuggestions';
import usePredictiveInput from './hooks/usePredictiveInput';
import useKeyEvent from './hooks/useKeyEvents';
import useSearchBar from './hooks/useSearchBar';
import { SearchBarContextType, Suggestions } from './types';
import { useLabels } from './hooks/useLabels';
import { usePacks } from './hooks/usePacks';

export const SearchBarContext = React.createContext<SearchBarContextType>(
    DefaultSearchBarContext,
);

export const useSearchBarContext = (): SearchBarContextType =>
    useContext(SearchBarContext);

export const SearchBarProvider = ({
    children,
    defaultPlaceholder = t`Search instruments, genres, keyword...`,
    trending,
    showOnlyFilterSuggestions,
}: {
    children: React.ReactNode;
    defaultPlaceholder?: string;
    trending?: Suggestions[];
    showOnlyFilterSuggestions: boolean;
}): React.ReactElement => {
    const isMobile = useBreakpoint('md');
    const recent = useUserRecentSearchSuggestions(showOnlyFilterSuggestions);
    const hasFilterApplied = useHasFilterApplied({});
    const { load: loadLabels } = useLabels();
    const { load: loadPacks } = usePacks();

    useEffect(() => {
        loadLabels();
    }, [loadLabels]);

    useEffect(() => {
        loadPacks();
    }, [loadPacks]);

    const { hasActiveFilters } = useCurrentSearchState();

    const {
        activeTabTotal,
        filterInstrumentsMeta,
        filterGenresMeta,
        filterSfxsMeta,
        filterTagsMeta,
    } = useSearchController();

    const {
        addFilter,
        removeFilter: searchControllerRemoveFilter,
        clearAll,
    } = useSearchModifiers();

    const {
        inputValue,
        hasTextInputValue,
        clearTextInput,
        placeholder,
        onInputChange,
    } = useInput({
        defaultPlaceholder,
        hasFilterApplied,
    });

    const { suggestions } = useSuggestions({
        searchInput: inputValue,
        hasActiveSearch: hasActiveFilters,
        activeTabTotal,
        showOnlyFilterSuggestions,
        filterInstrumentsMeta,
        filterGenresMeta,
        filterTagsMeta,
        filterSfxsMeta,
        hasFilterApplied,
        trending,
        recent,
    });

    const {
        selectPreviousSuggestion,
        selectNextSuggestion,
        selectedSuggestionIndex,
        resetSelectedSuggestion,
    } = useSelectSuggestion({
        suggestions,
    });

    const { predictiveModifiers, predictiveInput } = usePredictiveInput({
        inputValue,
        selectedSuggestionIndex,
        suggestions,
    });

    const {
        inputRef,
        applySuggestion,
        removeFilter,
        clearCtaLabel,
        handleClearCta,
        handleFocus,
        closeSuggestions,
        isSuggestionsOpen,
        handleApplyTextSearch,
    } = useSearchBar({
        addFilter,
        searchControllerRemoveFilter,
        clearAll,
        clearTextInput,
        hasTextInputValue,
        isMobile,
        resetSelectedSuggestion,
        inputValue,
        predictiveModifiers,
    });

    const { handleKeyPress, handleKeyDown } = useKeyEvent({
        onInputChange,
        predictiveInput,
        selectPreviousSuggestion,
        selectNextSuggestion,
        handleApplyTextSearch,
    });

    const isSearchBarDisabled = activeTabTotal === 0 && !hasActiveFilters;

    const context = {
        clearCtaLabel,
        hasFilterApplied,
        hasSuggestions: suggestions.length !== 0,
        hasTextInputValue,
        inputRef,
        inputValue,
        isModal: isMobile,
        isSuggestionsOpen,
        isSearchBarDisabled,
        placeholder,
        predictiveInput,
        selectedSuggestionIndex,
        suggestions,
        applySuggestion,
        closeSuggestions,
        handleApplyTextSearch,
        handleClearCta,
        handleFocus,
        handleKeyDown,
        handleKeyPress,
        onInputChange,
        removeFilter,
    };

    return (
        <SearchBarContext.Provider value={context}>
            {children}
        </SearchBarContext.Provider>
    );
};

export default SearchBarProvider;
